import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  CircularProgress,
  Alert
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

const VideoTrimmerModal = ({
  open,
  onClose,
  videoFile,
  onTrimComplete,
  maxDuration = 60,
  remainingDuration = 60
}) => {
  const [duration, setDuration] = useState(0);
  const [trimRange, setTrimRange] = useState([0, 0]);
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);

  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const urlRef = useRef(null); // Store URL in ref to prevent early cleanup

  // ✅ FIXED: Proper blob URL lifecycle management
  useEffect(() => {
    if (videoFile && open) {
      // Create blob URL
      const url = URL.createObjectURL(videoFile);
      urlRef.current = url; // Store in ref
      setVideoUrl(url);
      setLoading(true);
      setError(null);

      console.log("Created blob URL:", url);
    }

    // Cleanup function - only runs when modal closes or videoFile changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Only revoke if we have a URL and modal is closing
      if (urlRef.current && !open) {
        console.log("Revoking blob URL:", urlRef.current);
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [videoFile, open]);

  // Handle video loaded
  const handleVideoLoaded = () => {
    console.log("Video loaded successfully");

    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;

      if (isNaN(videoDuration) || videoDuration === 0) {
        setError("Invalid video duration");
        setLoading(false);
        return;
      }

      setDuration(videoDuration);

      // Set initial trim range
      const maxTrim = Math.min(videoDuration, remainingDuration);
      setTrimRange([0, maxTrim]);
      setLoading(false);

      console.log("Video duration:", videoDuration);
    }
  };

  // Handle video error
  const handleVideoError = (e) => {
    console.error("Video load error:", e);
    setError("Failed to load video. Please try another file.");
    setLoading(false);
  };

  // Update current time while playing
  useEffect(() => {
    if (playing && videoRef.current) {
      intervalRef.current = setInterval(() => {
        if (videoRef.current) {
          const time = videoRef.current.currentTime;
          setCurrentTime(time);

          // Stop at trim end
          if (time >= trimRange[1]) {
            videoRef.current.pause();
            videoRef.current.currentTime = trimRange[0];
            setPlaying(false);
            setCurrentTime(trimRange[0]);
          }
        }
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [playing, trimRange]);

  // Handle trim range change
  const handleTrimRangeChange = (event, newValue) => {
    const [start, end] = newValue;

    // Enforce max duration
    if (end - start > remainingDuration) {
      return;
    }

    setTrimRange(newValue);

    // Update video position if not playing
    if (videoRef.current && !playing) {
      videoRef.current.currentTime = start;
      setCurrentTime(start);
    }
  };

  // Play/Pause toggle
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
        setPlaying(false);
      } else {
        // Start from trim start if before range
        if (videoRef.current.currentTime < trimRange[0] ||
          videoRef.current.currentTime >= trimRange[1]) {
          videoRef.current.currentTime = trimRange[0];
        }
        videoRef.current.play().catch(err => {
          console.error("Play error:", err);
        });
        setPlaying(true);
      }
    }
  };

  // Seek to start
  const handleSeekToStart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = trimRange[0];
      setCurrentTime(trimRange[0]);
      if (playing) {
        videoRef.current.pause();
        setPlaying(false);
      }
    }
  };

  // Seek to end
  const handleSeekToEnd = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = trimRange[1];
      setCurrentTime(trimRange[1]);
      if (playing) {
        videoRef.current.pause();
        setPlaying(false);
      }
    }
  };

  // Handle trim confirmation with frontend recording
  const handleTrim = async () => {
    const [startTime, endTime] = trimRange;
    const trimDuration = endTime - startTime;

    if (trimDuration <= 0) {
      alert("Please select a valid trim range");
      return;
    }

    if (trimDuration > maxDuration) {
      alert(`Trimmed video cannot exceed ${maxDuration} seconds`);
      return;
    }

    if (trimDuration > remainingDuration) {
      alert(`You can only add ${remainingDuration.toFixed(1)} more seconds`);
      return;
    }

    setLoading(true);

    try {
      // 1. Setup playback for recording
      const video = videoRef.current;
      video.currentTime = startTime;

      // Wait for seek
      await new Promise(resolve => {
        video.onseeked = () => {
          video.onseeked = null; // Clean up
          resolve();
        };
      });

      // 2. Setup MediaRecorder
      const stream = video.captureStream();
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus' // Ensure broad compatibility
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      // 3. Record
      const recordingPromise = new Promise((resolve, reject) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/mp4' }); // Label as mp4 for compatibility
          // Create File object
          const trimmedFile = new File([blob], "trimmed.mp4", { type: "video/mp4" });

          // Add metadata for good measure
          trimmedFile.trimDuration = trimDuration;

          resolve(trimmedFile);
        };

        mediaRecorder.onerror = (e) => reject(e);
      });

      mediaRecorder.start();
      video.play();

      // 4. Monitoring stop condition
      const checkTime = () => {
        if (video.currentTime >= endTime) {
          video.pause();
          mediaRecorder.stop();
          // Remove listener
          video.removeEventListener('timeupdate', checkTime);
        }
      };

      video.addEventListener('timeupdate', checkTime);

      // Wait for recording to finish
      const finalFile = await recordingPromise;

      console.log("Trimmed file created:", finalFile.size, finalFile.type);
      onTrimComplete(finalFile, trimDuration);
      handleDialogClose();

    } catch (error) {
      console.error("Error creating trimmed file:", error);
      alert("Error processing video. Please try again.");
      setLoading(false);
    }
  };

  // Format time helper
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Close handler
  const handleDialogClose = () => {
    if (playing && videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }

    // Cleanup logic is handled by useEffect when 'open' prop changes
    // calling onClose will trigger the parent to set open=false

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={playing}
    >
      <DialogTitle>
        Trim Video
        <Typography sx={{ display: "block", color: "text.secondary" }} variant="caption">
          Select the portion of video you want to use
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Loading State */}
        {loading && !error && (
          <Box sx={{ py: 4,  alignItems: "center",  justifyContent: "center",  display: "flex" }}   >
            <CircularProgress size={30} />
            <Typography sx={{ ml: 2 }}>Loading video...</Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Video Content */}
        <Box sx={{ display: (loading && !error) ? 'none' : 'block' }}>
          {/* Video Player */}
          <Box sx={{ mb: 2 }} position="relative" bgcolor="black" borderRadius={1}>
            <video
              ref={videoRef}
              src={videoUrl}
              onLoadedMetadata={handleVideoLoaded}
              onError={handleVideoError}
              preload="metadata"
              playsInline
              style={{
                width: "100%",
                maxHeight: "400px",
                display: "block",
                borderRadius: "4px"
              }}
            />

            {/* Play/Pause Overlay */}
            {!loading && !error && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                sx={{ transform: "translate(-50%, -50%)" }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handlePlayPause}
                  sx={{
                    borderRadius: "50%",
                    minWidth: 64,
                    height: 64,
                    bgcolor: "rgba(0, 0, 0, 0.6)",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.8)"
                    }
                  }}
                >
                  {playing ? (
                    <PauseIcon sx={{ fontSize: 40, color: "white" }} />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: 40, color: "white" }} />
                  )}
                </Button>
              </Box>
            )}
          </Box>

          {/* Duration Warning */}
          {!loading && !error && trimRange[1] - trimRange[0] > remainingDuration && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Selected duration exceeds available space.
              Maximum: {remainingDuration.toFixed(1)}s
            </Alert>
          )}

          {/* Trim Controls */}
          {!loading && !error && (
            <Box sx={{ py: 2,  px: 2 }}  bgcolor="grey.50" borderRadius={1}>
              <Box sx={{ mb: 2,  alignItems: "center",  justifyContent: "space-between",  display: "flex" }}   >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Trim Range
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: trimRange[1] - trimRange[0] > remainingDuration ? "error" : "textSecondary" }}
                >
                  Available: {remainingDuration.toFixed(1)}s
                </Typography>
              </Box>

              {/* Slider */}
              <Slider
                value={trimRange}
                onChange={handleTrimRangeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={formatTime}
                min={0}
                max={duration}
                step={0.1}
                marks={[
                  { value: 0, label: '0:00' },
                  { value: duration, label: formatTime(duration) }
                ]}
                sx={{ mb: 2 }}
              />

              {/* Current Position Indicator */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Current Position: {formatTime(currentTime)}
                </Typography>
              </Box>

              {/* Time Display */}
              <Box sx={{ gap: 2,  justifyContent: "space-between",  display: "flex"  }}  >
                <Box flex={1} sx={{ textAlign: "center" }}>
                  <Typography variant="caption" sx={{ display: "block",  color: "text.secondary" }} >
                    Start Time
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {formatTime(trimRange[0])}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSeekToStart}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Go to Start
                  </Button>
                </Box>

                <Box flex={1} sx={{ textAlign: "center" }}>
                  <Typography variant="caption" sx={{ display: "block",  color: "text.secondary" }} >
                    Duration
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: trimRange[1] - trimRange[0] > remainingDuration ? "error" : "primary",  fontWeight: "bold" }}
                    
                  >
                    {formatTime(trimRange[1] - trimRange[0])}
                  </Typography>
                  <Typography sx={{ display: "block", mt: 1 }} variant="caption">
                    {trimRange[1] - trimRange[0] > remainingDuration
                      ? "⚠️ Exceeds limit"
                      : "✓ Within limit"}
                  </Typography>
                </Box>

                <Box flex={1} sx={{ textAlign: "center" }}>
                  <Typography variant="caption" sx={{ display: "block",  color: "text.secondary" }} >
                    End Time
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {formatTime(trimRange[1])}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSeekToEnd}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Go to End
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleDialogClose}>
          Cancel
        </Button>
        <Button
          onClick={handleTrim}
          variant="contained"
          disabled={
            loading ||
            error !== null ||
            trimRange[1] - trimRange[0] > remainingDuration ||
            trimRange[1] - trimRange[0] <= 0
          }
        >
          Use This Clip
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoTrimmerModal;

